import React, { Fragment } from "react";
import * as S from "sanctuary";
import { pathOr } from "ramda";
import { toFormattedDate, toFormattedDateNoTime } from "@/util/dates";
import { formatLineItems } from "@/util/common";
import {
  PaymentDetails,
  SolidDivider,
  Module,
  Text,
  Box,
  Stack,
  Switcher,
} from "@thecb/components";
import { getAmountByStatus } from "@/components/payment/payment.util";
import { BACKGROUND_GREY } from "@/constants/colors";
import { SEMI_BOLD, REGULAR } from "@/constants/fonts";
import { PAYMENT_STATUSES } from "../../../../sanctuary/typedefs";

export default ({
  payment,
  userTimezone,
  getServiceTypeText,
  getDescriptionKeys,
  getSubdescriptionKeys,
}) => {
  const agency = S.fromMaybe("")(payment.agency);
  const paymentSource = S.fromMaybe({})(payment.paymentSource);
  const paymentSourceName = paymentSource.name || "";
  const originalPaymentId = S.fromMaybe(0)(payment.originalPaymentId);
  const subtotal = S.fromMaybe("")(payment.subtotal);
  const serviceFee = S.fromMaybe("")(payment.serviceFee);
  const serviceType = S.fromMaybe("")(getServiceTypeText(payment.serviceType));
  const accountNumber = pathOr("", ["account", "value", "number", "value"], payment);
  const totalAmount = S.fromMaybe("")(payment.totalAmount);
  const createdAt = toFormattedDate(payment.createdAt, userTimezone);
  const fundedAt = toFormattedDateNoTime(payment.fundedAt);
  const status = S.fromEither("")(S.fromMaybe(S.Right(""))(payment.status));
  const paymentProcessTypeName = S.fromMaybe("")(payment.paymentProcessTypeName);
  const obligationSlug = S.fromMaybe("")(payment.obligationSlug);
  const descriptionKeys = getDescriptionKeys(obligationSlug);
  const subdescriptionKeys = getSubdescriptionKeys(obligationSlug);
  const formattedLineItems = formatLineItems(
    paymentProcessTypeName,
    payment.lineItems,
    descriptionKeys,
    subdescriptionKeys
  );
  const unavailableMessage = "Not available";
  const specificParams = S.fromMaybe({
    emailAddress: unavailableMessage,
    reversalReason: unavailableMessage,
  })(payment?.specificParams);
  const ACHReversalReason = S.fromMaybe(unavailableMessage)(
    specificParams.reversalReason
  );

  const displayLineItems =
    !!formattedLineItems.every((item) => !!item.description) &&
    payment.lineItems.length > 0 &&
    payment.lineItems.every((lineItem) => !!lineItem.customAttributes);

  const displayValue = (title, value) => (
    <Switcher
      breakpoint="700px"
      childGap="0.25rem"
      largeChild={2}
      largeChildSize={5}
    >
      <Text
        variant="pS"
        weight={SEMI_BOLD}
        dataQa={title}
        extraStyles="flex-basis: 17rem;"
      >
        {title}
      </Text>
      <Text variant="pS" weight={REGULAR}>
        {value}
      </Text>
    </Switcher>
  );
  return (
    <Module heading="Transaction Details" variant="small">
      <Box padding="1.5rem">
        <Stack childGap="0.5rem">
          {displayValue("Transaction date", createdAt)}
          {displayValue("Funded date", fundedAt)}
          {status === PAYMENT_STATUSES.ACH_RETURN &&
            displayValue("ACH return reason", ACHReversalReason)}
        </Stack>
      </Box>
      <SolidDivider />
      <Box padding="1.5rem">
        <Stack childGap="0.5rem">
          {displayValue("Agency name", agency)}
          {displayValue("Payment source name", paymentSourceName)}
          {displayValue("Service type", serviceType)}
          {displayValue("Account number", accountNumber)}
        </Stack>
      </Box>
      {!displayLineItems && (
        <Fragment>
          <SolidDivider />
          <Box padding="1.5rem" extraStyles="border-radius: 0.25rem;">
            <Stack childGap="0.5rem">
              {displayValue(
                "Subtotal",
                getAmountByStatus(subtotal, status, originalPaymentId)
              )}
              {displayValue(
                "Service fee",
                getAmountByStatus(serviceFee, status, originalPaymentId)
              )}
              {displayValue(
                "Total amount",
                getAmountByStatus(totalAmount, status, originalPaymentId)
              )}
            </Stack>
          </Box>
        </Fragment>
      )}
      {displayLineItems && (
        <Fragment>
          <SolidDivider />
          <Box
            padding="1.5rem"
            background={BACKGROUND_GREY}
            extraStyles="border-radius: 0.25rem;"
          >
            <Stack childGap="1rem">
              <PaymentDetails
                variant="small"
                hideTitle
                lineItems={formattedLineItems}
                fees={[
                  {
                    label: "Service Fee",
                    amount: serviceFee.replace(/,/g, "") * 100,
                  },
                ]}
                subtotal={subtotal.replace(/,/g, "") * 100}
                total={totalAmount.replace(/,/g, "") * 100}
              />
            </Stack>
          </Box>
        </Fragment>
      )}
    </Module>
  );
};
